import { useEffect, useMemo, useRef, useState } from 'react';

export function SuggestInput({
    value,
    onChange,
    options,
    placeholder = '/new-path',
}) {
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(0);
    // Search text typed since the field was last focused. Kept separate from
    // `value` so opening a field that already has a selection (e.g. a saved
    // policy link) shows every option instead of only ones matching it.
    const [query, setQuery] = useState('');
    const inputRef = useRef(null);
    const hostRef = useRef(null);

    // compact sizing
    const INPUT_H = 22; // inner input height (change here)
    const RING = 2; // gradient ring thickness
    const SPACER_H = INPUT_H + RING * 2;

    const filtered = useMemo(() => {
        const v = query.toLowerCase();
        const list = (options ?? []).filter((o) =>
            String(o).toLowerCase().includes(v)
        );
        const uniq = Array.from(new Set(list));
        // keep "/" first
        uniq.sort((a, b) => (a === '/' ? -1 : b === '/' ? 1 : 0));
        return uniq.slice(0, 200); // list can be long; scroll caps visible items
    }, [options, query]);

    useEffect(() => {
        if (!open) setHighlight(0);
    }, [open]);

    useEffect(() => {
        const onDocClick = (ev) => {
            if (!hostRef.current) return;
            if (!hostRef.current.contains(ev.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const select = (val) => {
        onChange(val);
        setOpen(false);
        requestAnimationFrame(() => inputRef.current?.focus());
    };

    const onKeyDown = (e) => {
        if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            setOpen(true);
            return;
        }
        if (!open) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((i) =>
                Math.min(i + 1, Math.max(filtered.length - 1, 0))
            );
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filtered[highlight]) select(filtered[highlight]);
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        // allow shrinking: w-0 flex-1 min-w-0; raise stacking when open
        <div
            ref={hostRef}
            className={`relative w-0 min-w-0 flex-1 ${open ? 'z-[999]' : ''}`}
        >
            {/* spacer so layout doesn’t move; the real UI is absolutely positioned */}
            <div style={{ height: `${SPACER_H}px` }} aria-hidden="true" />

            <div
                className={`absolute inset-x-0 top-0 ${
                    open ? 'z-[1000]' : 'z-40'
                }`}
            >
                {/* gradient wrapper becomes the unified border (input + dropdown) */}
                <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                    <div className="overflow-hidden rounded-[10px] bg-white">
                        {/* input */}
                        <div className="flex h-[22px] items-stretch">
                            <input
                                ref={inputRef}
                                className="flex-1 appearance-none border-0 bg-transparent px-1 text-[10px] leading-5
                           text-neutral-800 placeholder:text-neutral-400
                           focus:border-transparent focus:outline-none focus:ring-0 focus:ring-offset-0"
                                placeholder={placeholder}
                                value={value}
                                onFocus={(e) => {
                                    setOpen(true);
                                    setQuery('');
                                    e.target.select();
                                }}
                                onChange={(e) => {
                                    onChange(e.target.value);
                                    setQuery(e.target.value);
                                    setOpen(true);
                                }}
                                onKeyDown={onKeyDown}
                            />
                        </div>

                        {/* dropdown: ~5 items visible, then scroll */}
                        {open && filtered.length > 0 && (
                            <ul className="max-h-[120px] overflow-y-auto overscroll-contain py-1">
                                {filtered.map((opt, i) => (
                                    <li
                                        key={opt}
                                        className={`cursor-pointer px-1 py-0.5 text-[10px] ${
                                            i === highlight
                                                ? 'bg-neutral-50 text-neutral-900'
                                                : 'text-neutral-800 hover:bg-neutral-50'
                                        }`}
                                        onMouseDown={(e) => {
                                            e.preventDefault(); // keep focus
                                            select(opt);
                                        }}
                                        onMouseEnter={() => setHighlight(i)}
                                    >
                                        {opt}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
